import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { validateEmailExists, ValidateEmailExistsApiParameter } from "@waytek/Services/ValidateEmailExists";
import { ValidateEmailResponse } from "@waytek/Types/AvalaraValidationModel";

type ValidateEmailParameter = {
    billToAddress: BillToModel;
    useBillingAddress: boolean;
    shipToAddress: ShipToModel;
    onError?: (result: ValidateEmailResponse) => void;
} & HasOnSuccess<{ successful: boolean }>;

type HandlerType = ApiHandlerDiscreteParameter<ValidateEmailParameter, ValidateEmailExistsApiParameter>;

export const PopulateApiParameter: HandlerType = props => {
    const { billToAddress, shipToAddress } = props.parameter;

    const properties: { [key: string]: string } = {
        btAddress1: billToAddress.address1,
        btAddress2: billToAddress.address2,
        btAddress3: billToAddress.address3,
        btAddress4: billToAddress.address4,
        btAttention: billToAddress.attention,
        btCity: billToAddress.city,
        btCompanyName: billToAddress.companyName,
        btCountry: billToAddress.country?.id || "",
        btFax: billToAddress.fax,
        btFirstName: billToAddress.firstName,
        btLastName: billToAddress.lastName,
        btPhone: billToAddress.phone,
        btPostalCode: billToAddress.postalCode,
        btState: billToAddress.state?.id || "",
        btEmail: billToAddress.email,
        isSecondSubmission: "false",
    };

    const shipping = props.parameter.useBillingAddress ? billToAddress : shipToAddress;
    properties.stAddress1 = shipping.address1;
    properties.stAddress2 = shipping.address2;
    properties.stAddress3 = shipping.address3;
    properties.stAddress4 = shipping.address4;
    properties.stAttention = shipping.attention;
    properties.stCity = shipping.city;
    properties.stCompanyName = shipping.companyName;
    properties.stCountry = shipping.country?.id || "";
    properties.stFax = shipping.fax;
    properties.stFirstName = shipping.firstName;
    properties.stLastName = shipping.lastName;
    properties.stPhone = shipping.phone;
    properties.stPostalCode = shipping.postalCode;
    properties.stState = shipping.state?.id || "";
    properties.stEmail = shipping.email;

    props.apiParameter = {
        emailToCheck: billToAddress.email,
        properties,
    };
};

export const SendDataToApi: HandlerType = async props => {
    const result = await validateEmailExists(props.apiParameter);

    if (result.emailAlreadyExists) {
        props.parameter.onError?.(result);
        props.dispatch({
            type: "Pages/CheckoutShipping/SetBillingAddressFormErrors",
            formErrors: {
                email: siteMessage("AddressInfo_EmailAddress_AlreadyRegistered"),
            },
        });
        return false;
    }

    if (result.avalaraValidationFailed) {
        props.parameter.onError?.(result);
        return false;
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.({ successful: true });
};

export const chain = [PopulateApiParameter, SendDataToApi, ExecuteOnSuccessCallback];
const validateEmailAndAddress = createHandlerChainRunner(chain, "ValidateEmailAndAddress");
export default validateEmailAndAddress;
